import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Archive,
    Copy,
    Send,
    SquarePen,
    ClipboardList,
    Users,
    Filter,
    Search,
    ChevronsLeft,
    ChevronLeft,
    ChevronsRight,
    ChevronRight,
    ChevronDown,
    CheckCircle,
    AlertCircle,
    Loader2,
    Lock,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/app/back-button';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { DataTable } from '@/components/ui/data-table';
import AlertDialogArchiveProcedure from './_partials/alert-dialogs/AlertDialogArchiveProcedure';
import SheetFolderFilter from '@/pages/folders/index/_partials/sheets/SheetFolderFilter';
import SheetDuplicateProcedureTemplate from './_partials/sheets/SheetDuplicateProcedureTemplate';
import SheetVisibility from './_partials/sheets/SheetVisibility';
import { useProcedureTemplate } from '@/lib/hooks/use-procedure-template';
import { ProcedureDetailSkeleton } from '@/pages/procedures/index/_partials/ProcedureSkeletons';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

// Helper pour extraire les valeurs RGB d'une couleur (supporte hex et rgb)
function parseRgbColor(color) {
    if (!color) return { r: 52, g: 152, b: 219 };

    // Format rgb(r, g, b)
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
        return { r: rgbMatch[1], g: rgbMatch[2], b: rgbMatch[3] };
    }

    // Format hex #RRGGBB ou #RGB
    const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (hexMatch) {
        return {
            r: parseInt(hexMatch[1], 16),
            g: parseInt(hexMatch[2], 16),
            b: parseInt(hexMatch[3], 16),
        };
    }

    return { r: 52, g: 152, b: 219 }; // Fallback
}

/**
 * Génère une abréviation à partir du nom
 */
function generateAbbreviation(name) {
    if (!name) return '??';
    const words = name.split(' ').filter((w) => w.length > 2);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
}

/**
 * Formate une date ISO en format français (DD/MM/YYYY)
 */
function formatDate(dateString) {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    } catch {
        return dateString;
    }
}

/**
 * Formate une date ISO en heure (HH:MM)
 */
function formatTime(dateString) {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

/**
 * Transforme les subfolders (interventions) du template en données de rapports pour l'affichage
 */
function transformSubfoldersToReports(subfolders) {
    if (!subfolders || !Array.isArray(subfolders)) return [];

    return subfolders.map((subfolder) => {
        // Récupérer le statut: procedure_status (de InterventionResource) ou procedureReport.status (ancien format)
        const status = subfolder.procedure_status || subfolder.procedureReport?.status || 'completed';
        // Mapper le statut vers un label lisible
        const statusLabels = {
            draft: 'Brouillon',
            completed: i18n.t('procedures:finalise'),
            validated: i18n.t('procedures:finalise'),
        };

        return {
            id: subfolder.uniq_id || subfolder.id,
            name: subfolder.name || `Rapport #${subfolder.uniq_id?.slice(-6) || subfolder.id}`,
            realizedBy: {
                id: subfolder.user_uniq_id || '',
                name: subfolder.user_name || i18n.t('procedures:utilisateurInconnu'),
            },
            date: formatDate(subfolder.intervention_date || subfolder.created_at),
            time: formatTime(subfolder.intervention_date || subfolder.created_at),
            status: status === 'completed' ? 'validated' : status,
            statusLabel: statusLabels[status] || i18n.t('procedures:finalise'),
            version: subfolder.procedure_template_version || null,
            updatedAt: subfolder.updated_at || subfolder.created_at,
        };
    });
}

export default function ProcedureDetail() {
    const { t } = useTranslation();
    const { procedureId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState(null);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
    const [isDuplicateSheetOpen, setIsDuplicateSheetOpen] = useState(false);
    const [isVisibilitySheetOpen, setIsVisibilitySheetOpen] = useState(false);
    const [isTogglingLock, setIsTogglingLock] = useState(false);

    const handleBack = location.state?.from ? () => navigate(location.state.from) : undefined;

    // Contextes
    const { user } = useAuth();
    const { can, canOnDescendants } = usePermissions();
    const { getCache, setCache, invalidateCache, clearReportReturnTo } = useApiCache();

    // Réinitialiser le returnTo des rapports quand on arrive sur la page Procédure
    useEffect(() => {
        clearReportReturnTo();
    }, []);

    // État pour les collaborateurs et les sous-comptes
    const [collaborators, setCollaborators] = useState([]);
    const [filterCollaborators, setFilterCollaborators] = useState([]);
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);

    // Récupérer le template sans les rapports car on les charge séparément
    const { template, isLoading, error, notFound, refetch } = useProcedureTemplate(procedureId, {
        toLoad: ['allowedUsers' /*'subfolders'*/],
    });

    // Gestion des rapports paginés
    const [reportsData, setReportsData] = useState([]);
    const [reportsMeta, setReportsMeta] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [pageIndex, setPageIndex] = useState(0); // 0-based for TanStack Table
    const pageSize = 10;

    // Charger les rapports
    const loadReports = useCallback(async () => {
        setIsLoadingReports(true);
        try {
            const params = {
                page: pageIndex + 1,
                per_page: pageSize,
                search: searchQuery,
            };

            // Ajouter les filtres actifs si nécessaire
            if (activeFilters?.period && activeFilters.period !== 'all') {
                const periodDays = Number(activeFilters.period);
                if (periodDays && !Number.isNaN(periodDays) && periodDays > 0) {
                    params.days = periodDays;
                }
            }
            // Collaborateurs
            if (activeFilters?.collaborator && activeFilters.collaborator !== 'all') {
                params.users = Array.isArray(activeFilters.collaborator)
                    ? activeFilters.collaborator
                    : [activeFilters.collaborator];
            }
            // Entreprises subsidiaires
            if (Array.isArray(activeFilters?.subsidiaries) && activeFilters.subsidiaries.length > 0) {
                params.company_ids = activeFilters.subsidiaries;
            }

            const response = await horodatyApi.procedureTemplates.getReports(procedureId, params);

            if (response.data.success) {
                const transformed = transformSubfoldersToReports(response.data.reports);
                setReportsData(transformed);
                setReportsMeta(response.data.meta);
            }
        } catch (err) {
            console.error('Erreur chargement rapports:', err);
            toast.error(t('procedures:uneErreurEstSurvenueLorsDuChargementDes'));
        } finally {
            setIsLoadingReports(false);
        }
    }, [procedureId, pageIndex, searchQuery, activeFilters, t]);

    // Charger les rapports au montage et quand les paramètres changent
    useEffect(() => {
        loadReports();
    }, [loadReports]);

    // Refetch le template si on arrive depuis la page d'édition
    useEffect(() => {
        if (location.state?.refresh) {
            refetch();
            loadReports();
        }
    }, [location.state?.refresh]);

    // État pour les données d'accès (utilisateurs et entreprises assignés)
    const [accessData, setAccessData] = useState({
        users: [],
        companies: [],
        selectedUserIds: [],
        selectedCompanyIds: [],
    });

    // Charger les collaborateurs de l'entreprise
    /*const loadCollaborators = useCallback(async () => {
        const cacheKey = 'collaborators:company';
        const cached = getCache(cacheKey, 'collaborators');
        if (cached) {
            setCollaborators(cached);
            return;
        }

        setIsLoadingCollaborators(true);
        // Chargement des collaborateurs via l'API spécifique au template pour avoir les sélections
        try {
            const res = await horodatyApi.managerUsers();
            const list = res?.data?.data ?? [];
            const mapped = (Array.isArray(list) ? list : []).map((u) => ({
                id: String(u?.uniq_id ?? u?.id ?? ''),
                name: `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || u?.email || '—',
            }));
            setCollaborators(mapped);
            if (res?.data?.data !== undefined) {
                setCache(cacheKey, mapped, 'collaborators');
            }
        } catch (err) {
            console.error('Erreur lors du chargement des collaborateurs:', err);
            invalidateCache(cacheKey);
        } finally {
            setIsLoadingCollaborators(false);
        }
    }, [getCache, setCache, invalidateCache]);*/
    // Charger les collaborateurs de l'entreprise
    const loadCollaborators = useCallback(async () => {
        setIsLoadingCollaborators(true);
        // Chargement des collaborateurs via l'API spécifique au template pour avoir les sélections
        try {
            const response = await horodatyApi.procedureTemplates.getCollaborators(procedureId);
            const data = response.data;

            setAccessData({
                users: data.collaborators || [],
                companies: data.companies || [],
                selectedUserIds: data.selected_users || [],
                selectedCompanyIds: data.selected_companies || [],
            });

            // Mettre à jour la liste des collaborateurs pour le filtre
            const collaboratorsList = (data.collaborators || []).map((u) => ({
                id: String(u.uniq_id || u.id),
                name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email || '—',
            }));
            setCollaborators(collaboratorsList);
        } catch (err) {
            console.error('Erreur lors du chargement des accès:', err);
        } finally {
            setIsLoadingCollaborators(false);
        }
    }, [procedureId]);

    /*// Charger les collaborateurs quand le dialogue ou le filtre s'ouvre
    useEffect(() => {
        if (isAccessDialogOpen || isFilterSheetOpen) {
            loadCollaborators();
        }
    }, [isAccessDialogOpen, isFilterSheetOpen, loadCollaborators]);

    // Transformer les subfolders en données de rapports
    const reportsData = transformSubfoldersToReports(template?.subfolders);*/

    // Charger les collaborateurs au montage (uniquement si permission de gérer les accès)
    const canCreateProcedures = can('CREATE_PROCEDURES');
    // Le filtre « filiales » suit « Afficher les procédures » sur les sous-comptes
    const canListProceduresDescendants = canOnDescendants('LIST_PROCEDURES');
    useEffect(() => {
        if (canCreateProcedures) {
            loadCollaborators();
        }
    }, [loadCollaborators, canCreateProcedures]);

    // Charger les sous-comptes et les collaborateurs pour le filtre au montage
    useEffect(() => {
        const companyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;
        console.log('[ProcedureDetail] useEffect sous-comptes+collabs', { companyId, user: user?.uniq_id });
        if (!companyId) return;
        let isMounted = true;
        (async () => {
            // 1. Charger les collaborateurs de l'entreprise pour le filtre
            try {
                const companyUsersRes = await horodatyApi.getCompanyUsers(companyId);
                const users = companyUsersRes?.data?.data ?? [];
                const list = Array.isArray(users) ? users : [];
                console.log('[ProcedureDetail] getCompanyUsers résultat brut', {
                    count: list.length,
                    sample: list.slice(0, 2),
                });
                if (isMounted) {
                    const mapped = list
                        .map((u) => {
                            const uniq = u?.uniq_id ?? u?.uniqId ?? null;
                            if (!uniq) return null;
                            const first = u.first_name ?? u.firstName ?? '';
                            const last = u.last_name ?? u.lastName ?? '';
                            const name = `${first} ${last}`.trim() || u.name || u.email || String(uniq);
                            return { id: String(uniq), name };
                        })
                        .filter(Boolean);
                    console.log('[ProcedureDetail] filterCollaborators mappés', { count: mapped.length, mapped });
                    setFilterCollaborators(mapped);
                }
            } catch (err) {
                console.error('[ProcedureDetail] Erreur getCompanyUsers:', err);
            }
            // 2. Charger les sous-comptes si permission
            if (canListProceduresDescendants) {
                try {
                    const subsRes = await horodatyApi.subsidiaries.list(companyId);
                    const subs = subsRes?.data?.data ?? subsRes?.data ?? [];
                    console.log('[ProcedureDetail] subsidiaries chargées', {
                        count: Array.isArray(subs) ? subs.length : 0,
                    });
                    if (isMounted) setSubsidiaries(Array.isArray(subs) ? subs : []);
                } catch {
                    console.log('[ProcedureDetail] subsidiaries indisponibles');
                }
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [user, canListProceduresDescendants]);

    // Transformer les données du template pour l'affichage
    const assignedList = useMemo(() => {
        if (!accessData.users.length && !accessData.companies.length && !template?.allowed_users) return [];
        console.log(accessData);

        // Si on a chargé les données via getCollaborators, on les utilise en priorité
        if (accessData.users.length || accessData.companies.length) {
            const selectedCompanies = accessData.companies
                .filter((c) => accessData.selectedCompanyIds.includes(c.id || c.uniq_id))
                .map((c) => ({
                    id: c.uniq_id || c.id,
                    name: c.name || t('procedures:entreprise'),
                    avatar: null,
                    type: 'company',
                }));

            const selectedUsers = accessData.users
                .filter((u) => accessData.selectedUserIds.includes(u.id || u.uniq_id))
                .map((u) => ({
                    id: u.uniq_id || u.id,
                    name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || t('procedures:utilisateur'),
                    avatar: null,
                    type: 'user',
                }));

            return [...selectedCompanies, ...selectedUsers];
        }

        // Fallback sur allowed_users du template si disponibles
        return [].map((u) => ({
            id: u.uniq_id || u.id,
            name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || t('procedures:utilisateur'),
            avatar: null,
            type: 'user',
        }));
    }, [accessData, template, t]);

    const procedure = template
        ? {
              id: template.uniq_id,
              uniq_id: template.uniq_id,
              name: template.name,
              description: template.description || '',
              color: template.color || 'rgb(52, 152, 219)',
              abbreviation: generateAbbreviation(template.name),
              /*assignedUsers: (template.allowed_users || []).slice(0, 3).map((u) => ({
                  id: u.id || u.uniq_id,
                  name: u.first_name || u.name || 'Utilisateur',
                  avatar: null,
              })),
              additionalUsersCount: Math.max(0, (template.allowed_users || []).length - 3),*/
              assignedUsers: assignedList.slice(0, 3),
              additionalUsersCount: Math.max(0, assignedList.length - 3),
              configuration: template.configuration,
              is_locked: template.is_locked ?? false,
              created_by_user_uniq_id: template.created_by_user_uniq_id ?? null,
          }
        : null;

    const isCreator = procedure && user && procedure.created_by_user_uniq_id === (user.uniq_id ?? user.id);
    const isAdminType = template?.type === 'admin';
    const isSuperAdmin = user?.role === 'admin';
    const userCompanyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;
    const isOwnCompany = isSuperAdmin || (!!userCompanyId && template?.creator?.company_uniq_id === userCompanyId);

    // Verrouiller / déverrouiller le template
    const handleToggleLock = async () => {
        setIsTogglingLock(true);
        try {
            await horodatyApi.procedureTemplates.toggleLock(procedureId);
            refetch();
        } catch {
            toast.error(t('procedures:erreurLorsDuChangementDuVerrouillage'));
        } finally {
            setIsTogglingLock(false);
        }
    };

    // Archiver la procédure
    const handleArchive = async () => {
        setIsArchiving(true);
        try {
            await horodatyApi.procedureTemplates.archive(procedureId);
            toast.success(t('procedures:procedureArchiveeAvecSucces'));
            invalidateCache('procedure-templates');
            invalidateCache('procedure-templates-archives');
            setIsArchiveDialogOpen(false);
            navigate('/procedures');
        } catch (err) {
            toast.error(t('procedures:erreurLorsDeLArchivageDeLaProcedure'));
            console.error(err);
        } finally {
            setIsArchiving(false);
        }
    };

    // État de chargement
    if (isLoading) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader title={t('procedures:chargement')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <ProcedureDetailSkeleton />
            </div>
        );
    }

    // État d'erreur
    if (error) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader title={t('procedures:erreur')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">{t('procedures:erreurLorsDuChargementDeLaProcedure')}</p>
                    <Button variant="outline" color="neutral" onClick={() => refetch()}>
                        {t('procedures:reessayer')}
                    </Button>
                </div>
            </div>
        );
    }

    // Procédure non trouvée
    if (notFound || !procedure) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader title={t('procedures:nonTrouvee')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <div className="text-center py-12">
                    <p className="text-muted-foreground">{t('procedures:procedureNonTrouvee')}</p>
                    <Button variant="outline" color="neutral" onClick={() => navigate('/procedures')} className="mt-4">
                        {t('procedures:retourAuxProcedures')}
                    </Button>
                </div>
            </div>
        );
    }

    const rgb = parseRgbColor(procedure.color);
    const gradientStyle = {
        background: `linear-gradient(to right bottom, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.082), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03))`,
    };

    /*const filteredReports = reportsData
        .filter((report) => {
            // Filtre par recherche textuelle
            const query = searchQuery.toLowerCase();
            const matchesSearch =
                report.name.toLowerCase().includes(query) || report.realizedBy.name.toLowerCase().includes(query);
            if (!matchesSearch) return false;

            // Appliquer les filtres actifs
            if (activeFilters) {
                // Filtre par collaborateur
                if (activeFilters.collaborators && !activeFilters.collaborators.includes('all')) {
                    if (!activeFilters.collaborators.includes(report.realizedBy.id)) {
                        return false;
                    }
                }

                // Filtre par période
                if (activeFilters.period && activeFilters.period !== 'all') {
                    const days = parseInt(activeFilters.period, 10);
                    if (!isNaN(days) && report.date) {
                        try {
                            // Parser la date au format DD/MM/YYYY
                            const parts = report.date.split('/');
                            if (parts.length === 3) {
                                const reportDate = new Date(parts[2], parts[1] - 1, parts[0]);
                                const cutoffDate = new Date();
                                cutoffDate.setDate(cutoffDate.getDate() - days);
                                if (reportDate < cutoffDate) {
                                    return false;
                                }
                            }
                        } catch {
                            // Ignorer les erreurs de parsing
                        }
                    }
                }
            }

            return true;
        })
        .sort((a, b) => {
            // Trier par date de modification décroissante
            const dateA = a.updatedAt ? new Date(a.updatedAt) : new Date(0);
            const dateB = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
            return dateB - dateA;
        });*/

    const handleReportClick = (report) => {
        navigate(`/procedures/${procedureId}/reports/${report.id}`, { state: { returnTo: location.pathname } });
    };

    // Action buttons component (shared between mobile and desktop)
    const ActionButtons = ({ className = '' }) => {
    const { t } = useTranslation();
        // Procédure-type admin : seules Invitations et Visibilité sont accessibles (sauf Super Admin)
        const showAllActions = !isAdminType || isSuperAdmin;
        // Non-créateur + template verrouillé : ni archivage, ni duplication, ni édition (Super Admin exempté)
        const isLockedForUser = !isSuperAdmin && !isCreator && !!procedure.is_locked;
        // L'API n'archive et ne modifie que les procédures de sa propre entreprise :
        // une procédure partagée par la parente reste en lecture, mais se duplique et s'envoie.
        const canArchive = showAllActions && !isLockedForUser && isOwnCompany && can('ARCHIVE_PROCEDURES');
        const canDuplicate = showAllActions && !isLockedForUser && can('UPDATE_PROCEDURES');
        const canEdit = showAllActions && !isLockedForUser && isOwnCompany && can('UPDATE_PROCEDURES');
        // Une fille invite aussi sur les procédures de sa parente, même verrouillées
        const canInvite = can('UPDATE_PROCEDURES');
        // Sans « Éditer », Invitations prend toute la largeur avec son libellé
        const isInviteWide = !canEdit;

        return (
            <div className={`flex flex-col gap-[10px] w-full ${className}`}>
                {(canArchive || canDuplicate || canInvite || canEdit) && (
                    <div className="flex items-center gap-[12px] w-full">
                        {canArchive && (
                            <Button
                                variant="outline"
                                color="neutral"
                                size="xmd"
                                className="px-[12px] py-[12px] bg-card rounded-[12px] hover:bg-muted/50 border-border h-auto"
                                title={t('procedures:archiver')}
                                onClick={() => setIsArchiveDialogOpen(true)}
                                disabled={isArchiving}
                            >
                                <Archive className="h-5 w-5 text-foreground" />
                            </Button>
                        )}

                        {canDuplicate && (
                            <Button
                                variant="outline"
                                color="neutral"
                                size="xmd"
                                className="px-[12px] py-[12px] bg-card rounded-[12px] hover:bg-muted/50 border-border h-auto"
                                title={!hasValidConfiguration ? t('procedures:ajoutezAuMoinsUnChampALaProcedure') : t('procedures:dupliquer')}
                                onClick={() => setIsDuplicateSheetOpen(true)}
                                disabled={!hasValidConfiguration}
                            >
                                <Copy className="h-5 w-5 text-foreground" />
                            </Button>
                        )}

                        {canInvite && (
                            <Button
                                variant="outline"
                                color="neutral"
                                size="xmd"
                                className={
                                    isInviteWide
                                        ? 'flex-1 flex items-center justify-center gap-[8px] px-[16px] py-[12px] bg-card rounded-[12px] hover:bg-muted/50 border-border h-auto'
                                        : 'px-[12px] py-[12px] bg-card rounded-[12px] hover:bg-muted/50 border-border h-auto'
                                }
                                title={t('procedures:invitations')}
                                onClick={() => navigate(`/procedures/${procedureId}/invitations`)}
                                disabled={!hasValidConfiguration}
                            >
                                <Send className="h-5 w-5 text-foreground" />
                                {isInviteWide && (
                                    <span className=" text-[13px] text-foreground font-medium">{t('procedures:invitations')}</span>
                                )}
                            </Button>
                        )}

                        {canEdit && (
                            <Button
                                variant="outline"
                                color="neutral"
                                className="flex-1 flex items-center justify-center gap-[8px] px-[16px] py-[12px] bg-card rounded-[12px] hover:bg-muted/50 border-border h-auto"
                                onClick={() => navigate(`/procedures/${procedureId}/edit`)}
                            >
                                <SquarePen className="h-5 w-5 text-foreground" />
                                <span className=" text-[13px] text-foreground font-medium">{t('procedures:editer')}</span>
                            </Button>
                        )}
                    </div>
                )}

                {/* Non-créateur + template verrouillé → bandeau avertissement */}
                {isLockedForUser && (
                    <div className="flex items-center gap-[10px] w-full px-[14px] py-[12px] bg-yellow-500/10 rounded-[12px] border border-yellow-500/30">
                        <Lock className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                        <span className="text-[13px] text-yellow-700 dark:text-yellow-500">
                            {t('procedures:procedureVerrouilleeVousPouvezSeulementCreerUnRa')}
                        </span>
                    </div>
                )}

                {/* Switch de verrouillage — créateur ou Super Admin, pas pour les procédures-types sauf Super Admin */}
                {(isCreator || isSuperAdmin) && showAllActions && can('UPDATE_PROCEDURES') && (
                    <div className="flex items-center justify-between w-full px-[14px] py-[12px] bg-card rounded-[12px] border border-border">
                        <div className="flex items-center gap-[10px]">
                            <Lock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-[13px] text-foreground">
                                {procedure.is_locked ? t('procedures:procedureVerrouillee') : t('procedures:verrouillerLaProcedure')}
                            </span>
                        </div>
                        <Switch
                            checked={procedure.is_locked}
                            onCheckedChange={handleToggleLock}
                            disabled={isTogglingLock}
                        />
                    </div>
                )}
            </div>
        );
    };

    // Start report CTA button
    const hasValidConfiguration = template?.configuration?.sections?.some((section) => section.fields?.length > 0);

    const StartReportButton = ({ className = '' }) => (
        <Button
            variant="solid"
            color="secondary"
            size="cta"
            className={`w-full ${className}`}
            onClick={() => navigate(`/procedures/${procedureId}/reports/create`)}
            disabled={!hasValidConfiguration}
        >
            <ClipboardList className="h-5 w-5 text-secondary-foreground" />
            <span className=" text-[15px] text-secondary-foreground font-medium whitespace-normal">
                {hasValidConfiguration ? t('procedures:commencerUnRapport') : t('procedures:ajoutezDesChampsPourCommencer')}
            </span>
        </Button>
    );

    // Assigned users component
    const AssignedUsers = ({ textSize = '13px' }) => (
        <div className="flex items-center justify-between gap-[8px]">
            <div className="flex items-center gap-[6px] flex-1 min-w-0">
                <Users className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <div className="flex items-center gap-[4px] flex-wrap min-w-0">
                    {/*procedure.assignedUsers.map((user, index) => (
                        <span
                            key={user.id}
                            className=" text-muted-foreground whitespace-nowrap"
                            style={{ fontSize: textSize }}
                        >
                            {user.name}
                            {index < procedure.assignedUsers.length - 1 && ','}
                        </span>
                    ))}
                    {procedure.additionalUsersCount > 0 && (
                        <span className=" text-muted-foreground" style={{ fontSize: '11px' }}>
                            +{procedure.additionalUsersCount}
                        </span>
                    )*/}
                    {isLoadingCollaborators ? (
                        <span className="text-muted-foreground flex items-center gap-2" style={{ fontSize: textSize }}>
                            {t('procedures:chargement2')} <Loader2 className="h-3 w-3 animate-spin" />
                        </span>
                    ) : procedure.assignedUsers.length === 0 ? (
                        <span className="text-muted-foreground italic" style={{ fontSize: textSize }}>
                            {t('procedures:aucunCollaborateur')}
                        </span>
                    ) : (
                        <>
                            {procedure.assignedUsers.map((user, index) => (
                                <span
                                    key={`${user.type}-${user.id}`}
                                    className=" text-muted-foreground whitespace-nowrap"
                                    style={{ fontSize: textSize }}
                                >
                                    {user.name}
                                    {index < procedure.assignedUsers.length - 1 && ','}
                                </span>
                            ))}
                            {procedure.additionalUsersCount > 0 && (
                                <span className=" text-muted-foreground" style={{ fontSize: '11px' }}>
                                    +{procedure.additionalUsersCount}
                                </span>
                            )}
                        </>
                    )}
                </div>
            </div>
            <Button
                variant="plain"
                color="neutral"
                size="auto"
                className=" text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap flex-shrink-0 p-0 h-auto"
                style={{ fontSize: textSize }}
                disabled={isLoadingCollaborators}
                onClick={() => setIsVisibilitySheetOpen(true)}
            >
                {can('CREATE_PROCEDURES') ? t('procedures:gerer') : t('procedures:voir')}
            </Button>
        </div>
    );

    // Status badge component
    const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
        if (status === 'validated') {
            return (
                <div className="inline-flex items-center gap-[6px] bg-secondary/20 px-[10px] py-[4px] rounded-full">
                    <CheckCircle className="h-3.5 w-3.5 text-secondary" strokeWidth={2.5} />
                    <span className=" text-[12px] text-foreground">{t('procedures:finalise')}</span>
                </div>
            );
        }
        if (status === 'draft') {
            return (
                <div className="inline-flex items-center gap-[6px] bg-muted px-[10px] py-[4px] rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                    <span className=" text-[12px] text-muted-foreground">{t('procedures:brouillon')}</span>
                </div>
            );
        }
        return null;
    };

    // Définition des colonnes pour la DataTable
    const columns = [
        {
            id: 'mobileView',
            header: '',
            cell: ({ row }) => (
                <div className="flex items-center gap-[12px] w-full">
                    <div className="bg-muted rounded-[8px] h-[40px] w-[40px] flex items-center justify-center shrink-0">
                        <ClipboardList className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-[8px] mb-[2px]">
                            <p className=" text-[14px] text-foreground truncate">{row.original.realizedBy.name}</p>
                            {row.original.version && (
                                <span className="bg-border px-[6px] py-[1px] rounded-full text-[9px] text-muted-foreground">
                                    v{row.original.version}
                                </span>
                            )}
                            {row.original.status === 'validated' && (
                                <div className="bg-secondary rounded-full h-[18px] w-[18px] flex items-center justify-center shrink-0">
                                    <CheckCircle className="h-3 w-3 text-secondary-foreground" strokeWidth={2.5} />
                                </div>
                            )}
                            {row.original.status === 'draft' && (
                                <span className="bg-muted px-[8px] py-[2px] rounded-full text-[10px] text-muted-foreground">
                                    {t('procedures:brouillon')}
                                </span>
                            )}
                        </div>
                        <p className=" text-[11px] text-muted-foreground">
                            {row.original.date} à {row.original.time}
                        </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
            ),
            meta: {
                showOnMobileOnly: true,
                className: 'mobile-only md:hidden',
            },
        },
        {
            accessorKey: 'nameDesktop',
            header: t('procedures:nom'),
            cell: ({ row }) => (
                <div className="flex items-center gap-[12px]">
                    <div className="bg-muted rounded-[8px] h-[36px] w-[36px] flex items-center justify-center shrink-0">
                        <ClipboardList className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                    </div>
                    <span className=" text-[14px] text-foreground">{row.original.name}</span>
                </div>
            ),
            meta: {
                hideOnMobile: true,
            },
        },
        {
            accessorKey: 'realizedBy',
            header: t('procedures:realisePar'),
            cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.realizedBy.name}</span>,
            meta: {
                hideOnMobile: true,
            },
        },
        {
            accessorKey: 'date',
            header: t('procedures:date'),
            cell: ({ row }) => <span className=" text-[13px] text-muted-foreground">{row.original.date}</span>,
            meta: {
                hideOnMobile: true,
            },
        },
        {
            accessorKey: 'time',
            header: t('procedures:heure'),
            cell: ({ row }) => <span className=" text-[13px] text-muted-foreground">{row.original.time}</span>,
            meta: {
                hideOnMobile: true,
            },
        },
        {
            accessorKey: 'version',
            header: t('procedures:version'),
            cell: ({ row }) => (
                <span className="text-[13px] text-muted-foreground">
                    {row.original.version ? `v${row.original.version}` : '—'}
                </span>
            ),
            meta: {
                hideOnMobile: true,
            },
        },
        {
            accessorKey: 'status',
            header: t('procedures:statut'),
            cell: ({ row }) => <StatusBadge status={row.original.status} />,
            meta: {
                hideOnMobile: true,
            },
        },
        {
            id: 'actions',
            header: '',
            cell: () => (
                <div className="flex justify-end">
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
            ),
            meta: {
                hideOnMobile: true,
            },
        },
    ];

    const lockIndicator = procedure.is_locked ? (
        <>
            {/* Mobile : icône seule */}
            <Lock className="h-4 w-4 text-yellow-600 md:hidden" />
            {/* Desktop : badge avec texte */}
            <span className="hidden md:inline-flex items-center gap-[6px] text-[12px] text-yellow-700 dark:text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 px-[10px] py-[4px] rounded-full">
                <Lock className="h-3 w-3" />
                {t('procedures:verrouillee')}
            </span>
        </>
    ) : undefined;

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <PageHeader title={procedure.name} actions={lockIndicator}>
                <BackButton onClick={handleBack} />
            </PageHeader>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto scrollbar-hide md:overflow-hidden">
                <div className="md:flex md:h-full">
                    {/* Desktop Sidebar */}
                    <div className="desktop-only hidden md:block md:w-auto md:border-r md:border-border md:overflow-y-auto md:scrollbar-hide md:shrink-0 lg:min-w-[400px]">
                        <div className="md:h-full md:flex md:flex-col md:pt-[40px] md:px-[20px]" style={gradientStyle}>
                            <div className="w-full flex flex-col items-center justify-center">
                                {/* Icon */}
                                <div
                                    className="rounded-full h-[56px] w-[56px] flex items-center justify-center mb-[12px] shadow-md"
                                    style={{ backgroundColor: procedure.color }}
                                >
                                    <span className=" text-[20px] text-primary-foreground font-semibold">
                                        {procedure.abbreviation}
                                    </span>
                                </div>

                                {/* Title */}
                                <p className=" text-[24px] text-foreground text-center leading-tight font-medium mb-[16px]">
                                    {procedure.name}
                                </p>

                                {/* Assigned Users Card */}
                                <div className="bg-card rounded-[12px] px-[16px] py-[12px] w-full border border-border mb-[24px]">
                                    <AssignedUsers textSize="13px" />
                                </div>

                                {/* Action Buttons */}
                                <ActionButtons />

                                {/* Start Report Button */}
                                <StartReportButton className="mt-[12px]" />
                            </div>
                        </div>
                    </div>

                    {/* Right Content Area */}
                    <div className="flex-1 md:overflow-y-auto md:scrollbar-hide bg-[rgba(255,255,255,0)]">
                        <div className="px-[20px] py-[0px] md:px-[20px]">
                            {/* Mobile Card */}
                            <div className="mt-[20px] mb-[20px] mobile-only md:hidden">
                                <div className="bg-card rounded-[16px] overflow-hidden border border-border mx-auto">
                                    <div
                                        className="w-full flex flex-col items-center justify-center p-[20px]"
                                        style={gradientStyle}
                                    >
                                        {/* Icon */}
                                        <div
                                            className="rounded-full h-[56px] w-[56px] flex items-center justify-center mb-[12px] shadow-md"
                                            style={{ backgroundColor: procedure.color }}
                                        >
                                            <span className=" text-[20px] text-primary-foreground font-semibold">
                                                {procedure.abbreviation}
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <p className=" text-[24px] text-foreground text-center leading-tight font-medium mb-[21px]">
                                            {procedure.name}
                                        </p>

                                        {/* Action Buttons */}
                                        <ActionButtons className="mb-[12px]" />

                                        {/* Start Report Button */}
                                        <StartReportButton />
                                    </div>

                                    {/* Assigned Users Section */}
                                    <div className="px-[20px] py-[12px] border-t border-border/50">
                                        <AssignedUsers textSize="11px" />
                                    </div>
                                </div>
                            </div>

                            {/* Reports Section Title */}
                            <h2 className=" text-[16px] text-foreground font-medium mb-[12px] md:mt-[20px]">
                                {t('procedures:rapportsDeProcedure')}
                            </h2>

                            {/* Filter and Search */}
                            <div className="flex items-center gap-[12px] mb-[16px]">
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="gap-[6px] p-0 md:px-[16px] md:py-[10px] bg-card rounded-[8px] hover:bg-muted/50 border-border w-[40px] h-[40px] md:w-auto md:h-auto"
                                    onClick={() => setIsFilterSheetOpen(true)}
                                >
                                    <Filter className="w-4 h-4 text-muted-foreground" />
                                    <span className="hidden md:inline text-[14px] text-foreground">{t('procedures:filtrer')}</span>
                                    {activeFilters &&
                                        (() => {
                                            let count = 0;
                                            if (activeFilters.period && activeFilters.period !== 'all') count++;
                                            if (activeFilters.collaborator && activeFilters.collaborator !== 'all')
                                                count++;
                                            if (activeFilters.subsidiaries) count++;
                                            return count > 0 ? (
                                                <span className="flex items-center justify-center min-w-[20px] h-[20px] px-[6px] bg-primary rounded-[4px] text-[11px] text-primary-foreground font-medium">
                                                    {count}
                                                </span>
                                            ) : null;
                                        })()}
                                </Button>
                                <div className="relative flex-1">
                                    <Search className="absolute left-[16px] top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder={t('procedures:rechercherUnRapport')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full md:w-1/2 pl-[48px] pr-[16px] py-[12px] bg-card border border-border rounded-[12px]  text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-secondary transition-colors"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Reports DataTable */}
                        <div className="px-[20px]">
                            <div className="bg-card rounded-[12px] border border-border overflow-hidden">
                                <DataTable
                                    columns={columns}
                                    OLD_data={/*filteredReports*/ []}
                                    data={reportsData}
                                    onRowClick={handleReportClick}
                                    showPagination={true}
                                    manualPagination={true}
                                    pageCount={reportsMeta.last_page}
                                    currentPage={reportsMeta.current_page}
                                    onPageChange={(page) => setPageIndex(page - 1)}
                                    pageSize={reportsMeta.per_page}
                                    totalCount={reportsMeta.total}
                                    isLoading={isLoadingReports}
                                    emptyMessage={t('procedures:aucunRapportTrouve')}
                                    emptyIcon={<ClipboardList className="h-12 w-12 text-border" />}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialogs and Sheets */}
            <AlertDialogArchiveProcedure
                open={isArchiveDialogOpen}
                onOpenChange={setIsArchiveDialogOpen}
                onConfirm={handleArchive}
                isLoading={isArchiving}
            />

            <SheetFolderFilter
                open={isFilterSheetOpen}
                onClose={() => setIsFilterSheetOpen(false)}
                onSubmit={(filters) => {
                    setActiveFilters(filters);
                    setIsFilterSheetOpen(false);
                    setPageIndex(0);
                }}
                filters={activeFilters}
                collaborators={filterCollaborators}
                subsidiaries={subsidiaries}
            />

            <SheetDuplicateProcedureTemplate
                open={isDuplicateSheetOpen}
                onClose={() => setIsDuplicateSheetOpen(false)}
                procedureId={procedureId}
                procedureName={procedure.name}
                onSuccess={() => {
                    invalidateCache('procedure-templates');
                    navigate('/procedures');
                }}
            />

            <SheetVisibility
                open={isVisibilitySheetOpen}
                onClose={() => setIsVisibilitySheetOpen(false)}
                procedureId={procedureId}
                procedureName={procedure.name}
                onSuccess={() => loadCollaborators()}
            />
        </div>
    );
}
